<script setup lang="ts">
import { computed } from "vue";

import {
  centerFrac,
  findSymbol,
  polylinePx,
  rotationOf,
  scaleOf,
  type Point,
  type Shape,
} from "@/lib/drawingMarkup";

// Renders markup shapes over a page in PIXEL space (viewBox 0 0 w h), so stroke
// widths, text and symbols keep a sensible fixed size and don't distort. The
// parent sizes this to match the rendered page; panzoom scales it visually.
const props = defineProps<{ shapes: Shape[]; w: number; h: number }>();

const STROKE = 2.5;

function px(p: Point): [number, number] {
  return [p[0] * props.w, p[1] * props.h];
}

// Arrowhead triangle points for an arrow shape.
function arrowHead(from: Point, to: Point): string {
  const [x1, y1] = px(from);
  const [x2, y2] = px(to);
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = 12;
  const spread = 0.4;
  const a = [x2 - len * Math.cos(ang - spread), y2 - len * Math.sin(ang - spread)];
  const b = [x2 - len * Math.cos(ang + spread), y2 - len * Math.sin(ang + spread)];
  return `${x2},${y2} ${a[0]},${a[1]} ${b[0]},${b[1]}`;
}

const rendered = computed(() => props.shapes);
function line(s: Extract<Shape, { tool: "arrow" | "line" }>) {
  return { from: px(s.from), to: px(s.to) };
}
function rect(s: Extract<Shape, { tool: "rect" | "box" | "link" }>) {
  const [x1, y1] = px(s.topLeft);
  const [x2, y2] = px(s.bottomRight);
  return {
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    w: Math.abs(x2 - x1),
    h: Math.abs(y2 - y1),
  };
}
function circle(s: Extract<Shape, { tool: "circle" }>) {
  const [cx, cy] = px(s.center);
  return { cx, cy, rx: Math.abs(s.radius[0]) * props.w, ry: Math.abs(s.radius[1]) * props.h };
}
// Rotation transform (degrees around the shape's centre, in pixel space).
function rot(s: Shape): string | undefined {
  const deg = rotationOf(s);
  if (!deg) return undefined;
  const c = centerFrac(s);
  return `rotate(${deg} ${c[0] * props.w} ${c[1] * props.h})`;
}
</script>

<template>
  <svg
    class="pointer-events-none absolute inset-0"
    :width="w"
    :height="h"
    :viewBox="`0 0 ${w} ${h}`"
  >
    <g
      v-for="s in rendered"
      :key="s.id"
      :transform="rot(s)"
    >
      <template v-if="s.tool === 'arrow' || s.tool === 'line'">
        <line
          :x1="line(s).from[0]"
          :y1="line(s).from[1]"
          :x2="line(s).to[0]"
          :y2="line(s).to[1]"
          :stroke="s.color"
          :stroke-width="STROKE"
          stroke-linecap="round"
        />
        <polygon
          v-if="s.tool === 'arrow'"
          :points="arrowHead(s.from, s.to)"
          :fill="s.color"
        />
      </template>

      <rect
        v-else-if="s.tool === 'rect' || s.tool === 'box'"
        :x="rect(s).x"
        :y="rect(s).y"
        :width="rect(s).w"
        :height="rect(s).h"
        :stroke="s.color"
        :stroke-width="STROKE"
        :fill="s.tool === 'box' ? s.color : 'none'"
        :fill-opacity="s.tool === 'box' ? 0.18 : 0"
        rx="2"
      />

      <ellipse
        v-else-if="s.tool === 'circle'"
        :cx="circle(s).cx"
        :cy="circle(s).cy"
        :rx="circle(s).rx"
        :ry="circle(s).ry"
        :stroke="s.color"
        :stroke-width="STROKE"
        fill="none"
      />

      <polyline
        v-else-if="s.tool === 'freehand'"
        :points="polylinePx(s.points, w, h)"
        :stroke="s.color"
        :stroke-width="STROKE"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <text
        v-else-if="s.tool === 'text'"
        :x="px(s.position)[0]"
        :y="px(s.position)[1]"
        :fill="s.color"
        :font-size="16 * scaleOf(s)"
        font-weight="600"
        paint-order="stroke"
        stroke="#fff"
        stroke-width="3"
      >{{ s.text }}</text>

      <rect
        v-else-if="s.tool === 'link'"
        :x="rect(s).x"
        :y="rect(s).y"
        :width="rect(s).w"
        :height="rect(s).h"
        :stroke="s.color"
        :stroke-width="STROKE"
        stroke-dasharray="6 4"
        fill="none"
        rx="2"
      />

      <g
        v-else-if="s.tool === 'symbol'"
        :transform="`translate(${px(s.position)[0]} ${px(s.position)[1]}) scale(${scaleOf(s)}) translate(-12 -12)`"
      >
        <circle
          cx="12"
          cy="12"
          r="14"
          fill="#fff"
          :stroke="s.color"
          stroke-width="1.5"
        />
        <path
          :d="findSymbol(s.key)?.path"
          :stroke="s.color"
          stroke-width="1.8"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
    </g>
  </svg>
</template>
